import { AIGC_MATERIAL_REVIEW_OPTION } from '../../aigc-material-review';
import { clampTo } from '../../tools';
import type { RegistryEntry, UiOption } from '../types';

const seedance20BasicOptions: UiOption[] = [
    { key: 'audio', label: 'Audio-Visual Sync', type: 'switch', default: true, group: 'basic' },
    { key: 'web_search', label: 'Web Search', type: 'switch', default: false, group: 'basic' },
];

const outputFormatOption: UiOption = {
    key: 'output_format',
    label: 'Output Format',
    type: 'select',
    options: [
        { label: 'MP4', value: 'mp4' },
        { label: 'MOV', value: 'mov' },
    ],
    default: 'mp4',
    group: 'basic',
    remark: 'MP4 for general playback; MOV for high color precision (professional post). MOV may need VLC/mpv.',
};

const aigcMaterialReviewOption: UiOption = {
    key: AIGC_MATERIAL_REVIEW_OPTION,
    label: 'Enable Real-Person Auto Review',
    type: 'switch',
    default: true,
    group: 'inline',
    remark: 'After enabling real-person auto review, materials will be reviewed automatically so you can generate real-person content. This may slightly slow generation; if your materials do not involve real-person content, we recommend keeping it off.',
};

const highBitrateOption: UiOption = {
    key: 'high_bitrate',
    label: 'High Bitrate',
    type: 'switch',
    default: false,
    group: 'inline',
};

const toSeedance20ProviderOptions = (ui: Record<string, any>, withHighBitrate: boolean) =>
    ({
        audio: typeof ui.audio === 'boolean' ? ui.audio : true,
        web_search: typeof ui.web_search === 'boolean' ? ui.web_search : false,
        ...(withHighBitrate
            ? { high_bitrate: typeof ui.high_bitrate === 'boolean' ? ui.high_bitrate : false }
            : {}),
        [AIGC_MATERIAL_REVIEW_OPTION]:
            typeof ui[AIGC_MATERIAL_REVIEW_OPTION] === 'boolean'
                ? ui[AIGC_MATERIAL_REVIEW_OPTION]
                : true,
    } as unknown as Record<string, Record<string, any>>);

export const volcengineConfigs: Record<string, RegistryEntry> = {
    // Seedance 2.5：2.0 基础选项 + 输出格式
    'seedance-2.5': {
        options: [...seedance20BasicOptions, outputFormatOption, aigcMaterialReviewOption],
        toProviderOptions: (ui) =>
            ({
                ...toSeedance20ProviderOptions(ui, false),
                output_format: clampTo(
                    (typeof ui.output_format === 'string' ? ui.output_format : 'mp4'),
                    ['mp4', 'mov'] as const,
                    'mp4',
                ),
            } as unknown as Record<string, Record<string, any>>),
    },
    // Seedance 2.0 系列（百度主渠道）：无高码率
    ...Object.fromEntries(
        ['seedance-2.0', 'seedance-2.0-fast', 'seedance-2.0-mini'].map((id) => [
            id,
            {
                options: [...seedance20BasicOptions, aigcMaterialReviewOption],
                toProviderOptions: (ui: Record<string, any>) => toSeedance20ProviderOptions(ui, false),
            } satisfies RegistryEntry,
        ]),
    ),
    // 腾讯 uncensored 系列：高码率仅对这些生效
    ...Object.fromEntries(
        [
            'seedance-2.0-uncensored',
            'seedance-2.0-fast-uncensored',
            'seedance-2.0-mini-uncensored',
        ].map((id) => [
            id,
            {
                options: [...seedance20BasicOptions, highBitrateOption, aigcMaterialReviewOption],
                toProviderOptions: (ui: Record<string, any>) => toSeedance20ProviderOptions(ui, true),
            } satisfies RegistryEntry,
        ]),
    ),
    // Volcengine Seedance 1.5 Pro
    'seedance-1.5-pro': {
        options: [
            { key: 'audio', label: 'Audio-Visual Sync', type: 'switch', default: false, group: 'basic' },
            { key: 'cameraFixed', label: 'Camera Fixed', type: 'switch', default: false, group: 'basic' },
            { key: 'draft', label: 'Draft Mode', type: 'switch', default: false, group: 'basic' },
            { key: 'seed', label: 'Seed', type: 'number', min: 0, max: 2147483647, step: 1, group: 'basic' },
        ],
        toProviderOptions: (ui) =>
            ({
                audio: typeof ui.audio === 'boolean' ? ui.audio : false,
                draft: typeof ui.draft === 'boolean' ? ui.draft : false,
                cameraFixed: typeof ui.cameraFixed === 'boolean' ? ui.cameraFixed : false,
                seed: typeof ui.seed === 'number' && Number.isFinite(ui.seed) ? ui.seed : undefined,
            } as unknown as Record<string, Record<string, any>>),
    },
    // Volcengine Seedance 1.0 Pro / Pro Fast：Web Search + Seed（共享配置）
    ...Object.fromEntries(
        ['seedance-1.0-pro', 'seedance-1.0-pro-fast'].map((id) => [
            id,
            {
                options: [
                    { key: 'web_search', label: 'Web Search', type: 'switch', default: false, group: 'basic' },
                    { key: 'seed', label: 'Seed', type: 'number', min: 0, max: 2147483647, step: 1, group: 'advanced' },
                ],
                toProviderOptions: (ui: Record<string, any>) =>
                    ({
                        web_search: typeof ui.web_search === 'boolean' ? ui.web_search : false,
                        seed: typeof ui.seed === 'number' && Number.isFinite(ui.seed) ? ui.seed : undefined,
                    } as unknown as Record<string, Record<string, any>>),
            } satisfies RegistryEntry,
        ]),
    ),
};
